import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getPlatform, getAppVersion } from "@/lib/platform";
import { isStyleFree } from "@/data/designStyles";
import {
  evaluateLockedStyles,
  type LockedStylesFlag,
} from "@/lib/feature-flag-eval";

export * from "@/lib/feature-flag-eval";

const CACHE_TTL_MS = 5 * 60 * 1000;

interface CachedFlag {
  data: LockedStylesFlag;
  fetchedAt: number;
}

const flagCaches = new Map<string, CachedFlag>();
const inflights = new Map<string, Promise<LockedStylesFlag | null>>();

const fetchFlag = async (key: string): Promise<LockedStylesFlag | null> => {
  const cached = flagCaches.get(key);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.data;
  }
  if (inflights.has(key)) return inflights.get(key)!;

  const inflight = (async () => {
    try {
      const { data, error } = await supabase
        .from("feature_flags")
        .select(
          "enabled_global,enabled_for_admins,enabled_user_ids,min_enforce_android_version,min_enforce_ios_version,admin_bypass"
        )
        .eq("key", key)
        .maybeSingle();
      if (error || !data) return null;
      const flag: LockedStylesFlag = {
        enabled_global: !!data.enabled_global,
        enabled_for_admins: !!data.enabled_for_admins,
        enabled_user_ids: (data.enabled_user_ids as string[] | null) ?? [],
        min_enforce_android_version: data.min_enforce_android_version ?? null,
        min_enforce_ios_version: data.min_enforce_ios_version ?? null,
        admin_bypass:
          (data as { admin_bypass?: boolean | null }).admin_bypass ?? true,
      };
      flagCaches.set(key, { data: flag, fetchedAt: Date.now() });
      return flag;
    } finally {
      inflights.delete(key);
    }
  })();

  inflights.set(key, inflight);
  return inflight;
};

export const invalidateFlagCache = (key: string) => {
  flagCaches.delete(key);
};

export const invalidateLockedStylesCache = () => {
  invalidateFlagCache("locked_design_styles");
};

export interface UseFeatureFlagResult {
  loading: boolean;
  enforced: boolean;
  isAdmin: boolean;
  reason: string;
}

export const useFeatureFlagEnforced = (key: string): UseFeatureFlagResult => {
  const [loading, setLoading] = useState(true);
  const [enforced, setEnforced] = useState(false);
  const [reason, setReason] = useState<string>("no-flag");
  const [isAdmin, setIsAdmin] = useState(false);

  const evaluate = useCallback(async () => {
    setLoading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user ?? null;

      if (!user) {
        setEnforced(false);
        setReason("no-user");
        return;
      }

      const [flag, profileRes, roleRes, appVersion] = await Promise.all([
        fetchFlag(key),
        supabase
          .from("profiles")
          .select("subscription_tier")
          .eq("id", user.id)
          .maybeSingle(),
        supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
        getAppVersion(),
      ]);

      const admin = !!roleRes.data;
      setIsAdmin(admin);

      const result = evaluateLockedStyles({
        flag,
        userId: user.id,
        userTier: profileRes.data?.subscription_tier ?? "free",
        isAdmin: admin,
        platform: getPlatform(),
        appVersion,
      });

      setEnforced(result.enforced);
      setReason(result.reason);
    } catch (e) {
      console.warn(`useFeatureFlagEnforced("${key}") eval failed:`, e);
      setEnforced(false);
      setReason("error");
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    evaluate();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      evaluate();
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, [evaluate]);

  return { loading, enforced, isAdmin, reason };
};

export interface UseLockedStylesFlagResult {
  loading: boolean;
  enforced: boolean;
  isAdmin: boolean;
  isStyleLocked: (styleName: string) => boolean;
}

export const useLockedStylesFlag = (): UseLockedStylesFlagResult => {
  const { loading, enforced, isAdmin } = useFeatureFlagEnforced("locked_design_styles");

  const isStyleLocked = useCallback(
    (styleName: string) => enforced && !isStyleFree(styleName),
    [enforced]
  );

  return { loading, enforced, isAdmin, isStyleLocked };
};
